import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// common
import { existByScopeAndNamesRequest } from '../../common/actions';
// components
import { closeModal } from '../../components/modal/actions';
import { closeLoader, openLoader } from '../../components/loader/actions';
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSagas } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature equipment-service-invoice
import * as A from './actions';
import { SERVICE_PREFIX } from './settings';
import {
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

function* getItemListRequest(action) {
  try {
    yield put(A.setListLoading(true));

    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const pagination = yield select(makeSelectPagination());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['payload', 'guids'], action);

    const systemFields = [
      {
        freezed: false,
        sequence: 100,
        reference: false,
        name: `${SERVICE_PREFIX}.${GC.FIELD_TRUCK_GUID}`,
      },
      {
        sequence: 101,
        freezed: false,
        reference: false,
        name: `${SERVICE_PREFIX}.${GC.FIELD_TRAILER_GUID}`,
      },
      {
        sequence: 102,
        freezed: false,
        reference: false,
        name: `${SERVICE_PREFIX}.${GC.FIELD_GUID}`,
      },
    ];

    const reqBody = {
      ...pagination,
      guids,
      orderFields,
      systemFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      fields: G.getOrElse(reportParams, 'fields', []),
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.fleetEquipmentServiceInvoiceList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'getItemListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'getItemListRequest exception'));
  } finally {
    yield put(closeLoader());
    yield put(A.setListLoading(false));
  }
}

function* exportReportDataRequest({ payload }) {
  try {
    yield put(openLoader());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const { name, fields, fileType, orderFields, searchCriteria } = payload;

    const reqBody = {
      fields,
      orderFields,
      [GC.FIELD_REPORT_NAME]: name,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const options = {
      data: reqBody,
      resType: 'arraybuffer',
      params: { format: fileType },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.fleetEquipmentServiceInvoiceListExport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'handleExportReportDataSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException(error, 'handleExportReportDataSaga exception'));
  }
}

function* exportEquipmentServiceInvoiceToQuickBooksRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { guids } = payload;

    let options = {
      data: payload,
    };

    if (G.isNilOrEmpty(guids)) {
      const report = yield select(makeSelectUsedReport());

      options = {
        data: {
          ...payload,
          searchCriteria: G.getOrElse(report, 'searchCriteria', []),
        },
      };
    }

    const res = yield call(sendRequest, 'post', endpointsMap.fleetEquipmentServiceInvoiceToQuickBooks, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:request-processing');

      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'handleExportToQuickBooksSaga fail', true);
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleExportToQuickBooksSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(
  GC.FLEET_EQUIPMENT_SERVICE_INVOICE_REPORT,
  A,
  getItemListRequest,
  { createUpdateReportSuccessCallback },
);

function* getConfigsByNamesRequest() {
  try {
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: G.getAmousCurrentBranchGuidFromWindow(),
        names: R.join(',', [GC.TRUCK_DOCUMENT_TYPE, GC.TRAILER_DOCUMENT_TYPE]),
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getConfigsByNamesSuccess(G.mapConfigValuesByName(data)));
    } else {
      yield call(G.handleFailResponse, res, 'getConfigsByNamesRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getConfigsByNamesRequest exception');
  }
}

function* updateEquipmentServiceRequest({ payload }) {
  try {
    yield put(openLoader());

    const { truckGuid } = payload;

    const options = { data: payload };

    const endpoint = G.ifElse(
      R.isNotNil(truckGuid),
      endpointsMap.truckService,
      endpointsMap.trailerService,
    );

    const res = yield call(sendRequest, 'put', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest());
    } else {
      yield call(G.handleFailResponse, res, 'updateEquipmentServiceRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'updateEquipmentServiceRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* visitEquipmentServiceInvoicePageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield put(A.setInitialState());

    yield call(visitPageSaga, payload, GC.CHECK_VISIT_EQUIPMENT_SERVICE_INVOICE_PAGE);

    yield put(openLoader());

    yield put(existByScopeAndNamesRequest({
      nameToSave: 'qbAllowed',
      configNames: [GC.INVOICE_QB_AUTHORIZATION, GC.INVOICE_QB_CUSTOMER_OVERRIDE_URL],
    }));

    yield put(A.setReportPending());

    yield call(handleAvailableReportsRequest, { payload });
    yield call(getItemListRequest);
    yield call(getConfigsByNamesRequest);

    yield put(closeLoader());

    break;
  }
}

export function* equipmentServiceInvoiceWatcherSaga() {
  yield takeLatest(GC.VISIT_EQUIPMENT_SERVICE_INVOICE_PAGE, visitEquipmentServiceInvoicePageSaga);
  // report
  yield takeLatest(A.getItemListRequest, getItemListRequest);
  yield takeLatest(A.exportReportDataRequest, exportReportDataRequest);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  // equipment-service
  yield takeLatest(A.updateEquipmentServiceRequest, updateEquipmentServiceRequest);
  // quickbooks
  yield takeLatest(A.exportEquipmentServiceInvoiceToQuickBooksRequest, exportEquipmentServiceInvoiceToQuickBooksRequest);
}

export default equipmentServiceInvoiceWatcherSaga;
